import classNames from "classnames";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useModalControl } from "../../customeHooks/useModalControl";
import { authSliceActions } from "../../redux/features/auth/auth.slice";
import { AuthProfile } from "../../redux/features/auth/auth.types";
import { useGetEventsQuery } from "../../redux/features/event/event.api";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { persistor } from "../../redux/provider";
import { clientProfileCookieKey } from "../../utils/constants";
import { getCookie, regexText } from "../../utils/helpers";
import { Button } from "../buttons/button";
import { ProfileIcon } from "../cards/profilePic";
import Dropdown from "../dropdown/dropdown";
import { Icon } from "../icon/icon";
import { MobileNavModal } from "../modal/app_modals/mobileNavModal";
import { ScrollToTop } from "../scrollToTop";

const testArray = [
  /^\/events\/[^/]+\/published$/,
  /^\/events\/[^/]+\/add-payout$/,
  /^\/complete-profile$/,
  /^\/events\/[^/]+\/$/,
  /^\/events\/[^/]+\/preview$/,
];

const mobileTestArray = [...testArray, /^\/events\/[^/]+$/];

const navs = [
  {
    title: "Home",
    to: "/home",
    icon: "home",
  },
  {
    title: "Events",
    to: "/events",
    icon: "calendar",
  },

  {
    title: "Discounts",
    to: "/discounts",
    icon: "circle-percent",
  },
];

export const AppLayout = ({ children }: { children: ReactNode }) => {
  const [scrollProgress, setScrollProgress] = useState(0);

  const pageRef = useRef<HTMLDivElement>(null);

  const location = useLocation();

  const { scrollYProgress } = useScroll({ container: pageRef });

  useMotionValueEvent(scrollYProgress, "change", (val) => {
    setScrollProgress(val);
  });

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { handleModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl();

  const { isSignedIn, profile } = useAppSelector((state) => state.auth);

  const { data: events } = useGetEventsQuery(
    { page: 1, limit: 1 },
    { refetchOnMountOrArgChange: true, skip: !isSignedIn },
  );

  const dropDownOptions = [
    {
      icon: "avatar",
      label: "View Profile",
      onClick: () => {
        navigate("/settings");
      },
    },
    {
      icon: "logout",
      label: "Logout",
      onClick: () => {
        dispatch(authSliceActions.signUserOut());

        persistor.purge();
      },
    },
  ];

  useEffect(() => {
    if (pageRef.current) {
      pageRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });

      setScrollProgress(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      (events?.data.totalCount ?? 0) > 0 ||
      profile?.userType === "organiser"
    ) {
      dispatch(authSliceActions.markCreateEvent());
    } else if ((events?.data.totalCount ?? 0) === 0) {
      dispatch(authSliceActions.unMarkCreateEvent());
    }
    // eslint-disable-next-line
  }, [events]);

  return (
    <>
      <ScrollToTop ref={pageRef} />
      <div className="flex h-screen max-h-[100dvh] flex-col bg-light-grey-bg">
        {/* Top nav  */}
        <div
          className={classNames(
            "border-b border-grey-100 py-6 md:border-b-0 md:py-6",
            scrollProgress > 0
              ? "md:!border-b md:shadow-sm md:shadow-grey-900/20"
              : "",
            "!z-[998] transition-all duration-300",
            "bg-light-grey-bg",
          )}
        >
          <div className="max-width">
            <div className="relative flex items-center justify-between gap-4">
              <Link
                to={"/home"}
                className="max-w-[80px] md:absolute md:left-0 md:max-w-max"
              >
                <img
                  src="/assets/logo_full.svg"
                  className="w-full"
                  alt="logo"
                />
              </Link>

              {isSignedIn &&
              !regexText(location.pathname, testArray) &&
              ![""].includes(location.pathname) ? (
                <div className="hidden flex-1 items-center justify-center gap-4 md:flex">
                  {navs.map((nav, idx) => {
                    const key = `${idx}-${nav.to}`;

                    return (
                      <NavLink
                        key={key}
                        to={nav.to ?? ""}
                        className={({ isActive }) => {
                          return `soft-shrink flex items-center gap-2.5 rounded-2xl px-5 py-3 font-bold ${isActive && "bg-white"} `;
                        }}
                      >
                        <Icon
                          svg={nav.icon}
                          height={20}
                          width={20}
                          className=""
                        />
                        {nav.title}
                      </NavLink>
                    );
                  })}
                </div>
              ) : (
                <span className="opacity-0">p</span>
              )}

              {isSignedIn ? (
                <>
                  <ProfileDropdown
                    className={"hidden md:absolute md:right-0 md:inline-block"}
                    useCookieProfile={false}
                    dropDownOptions={dropDownOptions}
                  />

                  <button
                    type="button"
                    className="flex items-center gap-1 rounded-full bg-white px-3 py-2 text-sm font-bold md:absolute md:right-0 md:hidden"
                    onClick={() => {
                      handleModalOpen("mobileNavModal");
                    }}
                  >
                    <Icon svg="caret-down" width={24} height={24} />
                    <span>Menu</span>
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-3 md:absolute md:right-0">
                  <Button
                    type="button"
                    text="Log in"
                    onClick={() => {
                      navigate("/login");
                    }}
                    className="min-w-max max-w-fit"
                    variation="plain-grey"
                  />

                  <Button
                    type="button"
                    text="Sign Up"
                    onClick={() => {
                      navigate("/signup");
                    }}
                    className="min-w-max max-w-fit"
                    variation="primary"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* main screen  */}
        <div
          className="max-width flex flex-1 overflow-y-scroll py-8"
          ref={pageRef}
          id="appMainBody"
        >
          <div className="h-max w-full">{children}</div>
        </div>

        {/* bottom mobile nav  */}
        <div
          className={classNames(`max-width relative py-4 md:hidden`, {
            "!hidden": regexText(location.pathname, mobileTestArray),
          })}
        >
          {["/events", "/home"].includes(location.pathname) ? (
            <CreateEventButton />
          ) : undefined}

          <div className="grid grid-flow-row grid-cols-[repeat(auto-fit,_minmax(100px,1fr))] items-center">
            {navs.map((nav, idx) => {
              const key = `${idx}-${nav.to}`;

              return (
                <NavLink
                  key={key}
                  to={nav.to ?? ""}
                  className={({ isActive }) => {
                    return `soft-shrink flex items-center justify-center gap-2.5 rounded-2xl px-5 py-3 text-sm font-bold ${isActive && "bg-white"} `;
                  }}
                >
                  <Icon
                    svg={nav.icon}
                    height={20}
                    width={20}
                    className="min-w-max"
                  />
                  {nav.title}
                </NavLink>
              );
            })}
          </div>
        </div>

        <MobileNavModal
          open={modalOpen && modal === "mobileNavModal"}
          handleClose={handleModalClose}
          options={dropDownOptions}
        />
      </div>
    </>
  );
};

const CreateEventButton = () => {
  const [collapseButton, setCollapseButton] = useState(false);

  const navigate = useNavigate();

  const { hasCreatedEvent } = useAppSelector((state) => state.auth);

  useEffect(() => {
    const myTimeout = setTimeout(() => {
      setCollapseButton(true);
    }, 1500);

    return () => {
      if (myTimeout) {
        clearTimeout(myTimeout);
      }
    };
  });

  return (
    <Button
      text={collapseButton ? "" : "Create Event"}
      icon="plus"
      type="button"
      className={classNames(
        "!absolute !bottom-[calc(100%+12px)] !right-[30px] md:hidden",
        {
          "!w-[162px]": !collapseButton,
          "!size-[50px] overflow-hidden !p-0": collapseButton,
        },
        "transition-all duration-500",
      )}
      onClick={() => {
        if (hasCreatedEvent) {
          navigate("/event/new");
        } else {
          navigate("/complete-profile");
        }
      }}
      iconDimension={collapseButton ? 20 : 16}
    />
  );
};

export const ProfileDetails = ({
  className = "",
  profileClassName = "!p-3",
  profileDimension = 64,
  useCookieProfile = false,
}: {
  className?: string;
  profileClassName?: string;
  profileDimension?: number;
  useCookieProfile?: boolean;
}) => {
  const { profile: stateProfile } = useAppSelector((state) => state.auth);

  const cookieProfile: AuthProfile = getCookie(clientProfileCookieKey)
    ? JSON.parse(getCookie(clientProfileCookieKey)!)
    : undefined;

  const profile = useCookieProfile ? cookieProfile : stateProfile;

  return (
    <div
      className={`flex flex-col items-center justify-center rounded-md bg-white p-3 text-center ${className}`}
    >
      <ProfileIcon
        dimension={profileDimension}
        className={profileClassName}
        img={undefined}
      />

      <p className="mt-2 text-base font-bold">
        {profile?.firstName} {profile?.lastName}
      </p>
      <p className="text-sm text-grey-500">{profile?.email}</p>
    </div>
  );
};

export const ProfileDropdown = ({
  dropDownOptions,
  useCookieProfile = false,
  className = "",
}: {
  dropDownOptions: {
    icon: string;
    label: string;
    onClick: () => void;
  }[];
  useCookieProfile?: boolean;
  className?: string;
}) => {
  const { profile: stateProfile } = useAppSelector((state) => state.auth);

  const cookieProfile: AuthProfile = getCookie(clientProfileCookieKey)
    ? JSON.parse(getCookie(clientProfileCookieKey)!)
    : undefined;

  const profile = useCookieProfile ? cookieProfile : stateProfile;

  return (
    <Dropdown
      menuItems={[
        {
          name: (
            <ProfileDetails
              className="!cursor-default !items-start !justify-start !p-3"
              profileDimension={32}
              profileClassName="!p-[6px]"
              useCookieProfile={useCookieProfile}
            />
          ),
          onClick: (e) => {
            e?.preventDefault();
            e?.stopPropagation();
          },
        },
        ...dropDownOptions.map((option) => {
          return {
            name: (
              <p className="soft-shrink flex items-center gap-2 !rounded-md !bg-white p-3 text-left !font-bold">
                <Icon
                  svg={option.icon}
                  width={20}
                  height={20}
                  className="!min-w-fit text-grey-400"
                />
                <span>{option.label}</span>
              </p>
            ),
            onClick: option.onClick,
          };
        }),
      ]}
      className={className}
      menuClassName="!gap-1 !border-0 !bg-grey-50 !p-1 !min-w-[300px]"
      menuItemsClassName="border-none !text-base !bg-transparent !p-0"
      position="bottom end"
    >
      <button className="flex w-fit items-center gap-1">
        <p className="hidden max-w-[154px] items-center gap-[6px] rounded-full bg-white py-2.5 pl-2 pr-3 text-base font-bold lg:flex">
          <ProfileIcon img="" dimension={32} className="" />

          <span className="line-clamp-1 block text-ellipsis">
            {profile?.firstName}
          </span>
        </p>

        <Icon
          svg="caret-down"
          width={52}
          height={52}
          className="rounded-full bg-white p-[14px] text-black"
        />
      </button>
    </Dropdown>
  );
};
